import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Loader2, ArrowLeft } from "lucide-react";
import { usePackages } from "../hooks/usePackages";
import { PackageCard } from "../components/PackageCard";
import { Stagger, StaggerItem, Reveal } from "../components/Reveal";
import { CTABand } from "../components/sections";
import { IMAGES } from "../data/site";

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const { data: all = [], isLoading } = usePackages();

  useEffect(() => setQ(params.get("q") || ""), [params]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter((p) =>
      `${p.title} ${p.subtitle} ${p.region} ${p.type} ${p.summary} ${(p.highlights || []).join(" ")}`
        .toLowerCase()
        .includes(s)
    );
  }, [q, all]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="pt-28 md:pt-32 min-h-screen">
      <section className="ll-container">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[#4A5D58] hover:text-[#0A221C] text-sm mb-6"><ArrowLeft size={16} /> Back</button>
        <p className="eyebrow text-[#f7ad2d]">Search</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold mt-3">Find your journey</h1>

        <form onSubmit={submit} className="relative max-w-2xl mt-8">
          <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#4A5D58]" />
          <input
            data-testid="search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search destinations, regions, tours..."
            className="w-full bg-white border border-black/10 rounded-full pl-14 pr-32 py-4 text-lg focus:outline-none focus:border-[#4ef72d] shadow-sm"
          />
          <button data-testid="search-submit" type="submit" className="btn-primary absolute right-2 top-1/2 -translate-y-1/2 !py-2.5 !px-6">Search</button>
        </form>
      </section>

      <section className="ll-container py-12">
        <p className="text-[#4A5D58] mb-8" data-testid="search-count">
          {isLoading ? "Searching..." : `${results.length} ${results.length === 1 ? "result" : "results"}${q ? ` for “${q}”` : ""}`}
        </p>
        {isLoading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>
        ) : results.length ? (
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {results.map((p) => <StaggerItem key={p.slug}><PackageCard pkg={p} /></StaggerItem>)}
          </Stagger>
        ) : (
          <Reveal className="text-center py-16">
            <img src={IMAGES.himalaya} alt="No results" className="w-40 h-40 object-cover rounded-3xl mx-auto opacity-70" />
            <p className="text-[#4A5D58] text-lg mt-6">No tours matched “{q}”. Try a different destination, or <a href="/contact" className="text-[#0A221C] font-semibold underline decoration-[#4ef72d]">tell us your dream trip</a>.</p>
          </Reveal>
        )}
      </section>

      <div className="pb-24"><CTABand /></div>
    </div>
  );
}
